import { useNavigate } from 'react-router';
import { Avatar } from '@/components/ui/Avatar';
import { useIsDesktop } from '@/hooks/useAppEffects';
import { useChannelPermissions } from '@/lib/permissions';
import { voiceManager } from '@/realtime/voice';
import { useVoice } from '@/stores/realtime';
import { useServerChannels } from '@/stores/servers';
import { useUser } from '@/stores/users';

function StackedAvatar({ userId }: { userId: string }) {
  const user = useUser(userId);
  return <Avatar user={user} size={28} className="rounded-full ring-2 ring-accent-2-soft" decorate={false} />;
}

/**
 * On phones the voice channels are tucked away in the drawer, so the busiest one
 * in this server shows as a pill above the chat: who's there, and a Join button.
 */
export function VoiceBanner({ serverId }: { serverId: string }) {
  const isDesktop = useIsDesktop();
  const channels = useServerChannels(serverId);
  const participants = useVoice((s) => s.participants);
  const myChannelId = useVoice((s) => s.channelId);
  const busiest = channels
    .filter((c) => c.type === 'VOICE' && c.id !== myChannelId && (participants[c.id]?.length ?? 0) > 0)
    .sort((a, b) => participants[b.id]!.length - participants[a.id]!.length)[0];
  const perms = useChannelPermissions(busiest?.id);
  const navigate = useNavigate();
  if (isDesktop || !busiest) return null;
  const people = participants[busiest.id]!;

  const join = () => {
    navigate(`/app/${serverId}/${busiest.id}`);
    void voiceManager.join(busiest.id, serverId);
  };

  return (
    <div className="shrink-0 px-4 pb-2">
      <div className="flex items-center gap-2.5 rounded-full bg-accent-2-soft py-2 pr-2 pl-2.5">
        <div className="flex -space-x-2">
          {people.slice(0, 3).map((p) => (
            <StackedAvatar key={p.userId} userId={p.userId} />
          ))}
        </div>
        <p className="min-w-0 flex-1 truncate text-[13px] text-accent-2-ink">
          <strong>{busiest.name}</strong> · {people.length} talking
        </p>
        {perms.can('CONNECT') && (
          <button type="button" onClick={join} className="shrink-0 rounded-full bg-accent-2-ink px-3.5 py-1.5 font-display text-[13px] text-surface transition-[filter] hover:brightness-110">
            Join
          </button>
        )}
      </div>
    </div>
  );
}
